import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Lock, Inbox, MessageSquare, RefreshCw, LogOut, Mail, Phone, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';
import Logo from '../components/Logo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(d) {
  try { return new Date(d).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return d; }
}

export default function Admin() {
  const { toast } = useToast();
  const [token, setToken] = useState(() => localStorage.getItem('mf_admin_token') || '');
  const [authed, setAuthed] = useState(false);
  const [pw, setPw] = useState('');
  const [loading, setLoading] = useState(false);
  const [estimates, setEstimates] = useState([]);
  const [messages, setMessages] = useState([]);

  const load = useCallback(async (t) => {
    setLoading(true);
    try {
      const cfg = { headers: { 'X-Admin-Token': t } };
      const [e, m] = await Promise.all([
        axios.get(`${API}/estimates`, cfg),
        axios.get(`${API}/contact`, cfg)
      ]);
      setEstimates(e.data);
      setMessages(m.data);
      setAuthed(true);
      localStorage.setItem('mf_admin_token', t);
    } catch (err) {
      setAuthed(false);
      localStorage.removeItem('mf_admin_token');
      toast({ title: 'Access denied', description: 'Incorrect password. Please try again.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { if (token) load(token); /* eslint-disable-next-line */ }, []);

  const submit = (e) => {
    e.preventDefault();
    setToken(pw);
    load(pw);
  };

  const logout = () => {
    setAuthed(false); setToken(''); setPw('');
    localStorage.removeItem('mf_admin_token');
  };

  if (!authed) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6 bg-brand-cream">
        <form onSubmit={submit} className="w-full max-w-md bg-white rounded-2xl border border-brand-line shadow-sm p-9">
          <div className="flex justify-center mb-6"><Logo /></div>
          <div className="w-14 h-14 rounded-2xl bg-brand-clay/10 text-brand-clay flex items-center justify-center mx-auto"><Lock size={24} /></div>
          <h1 className="font-display text-2xl font-semibold text-brand-charcoal text-center mt-5">Admin Inbox</h1>
          <p className="text-brand-stone text-center text-sm mt-2">Enter your password to view submissions.</p>
          <div className="mt-6 space-y-2">
            <Label htmlFor="pw">Password</Label>
            <Input id="pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="••••••••" className="h-12" autoFocus />
          </div>
          <Button type="submit" disabled={loading} className="w-full mt-5 bg-brand-clay hover:bg-brand-clayDark text-white rounded-full h-12">{loading ? 'Checking…' : 'Sign In'}</Button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] bg-brand-cream">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl font-semibold text-brand-charcoal">Admin Inbox</h1>
            <p className="text-brand-stone mt-1 text-sm">{estimates.length} estimate request(s) · {messages.length} message(s)</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => load(token)} disabled={loading} className="rounded-full border-brand-charcoal/20 text-brand-charcoal hover:bg-brand-charcoal hover:text-brand-ivory"><RefreshCw size={16} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh</Button>
            <Button variant="outline" onClick={logout} className="rounded-full border-brand-charcoal/20 text-brand-charcoal hover:bg-brand-charcoal hover:text-brand-ivory"><LogOut size={16} className="mr-1.5" /> Sign Out</Button>
          </div>
        </div>

        <Tabs defaultValue="estimates">
          <TabsList className="bg-white border border-brand-line">
            <TabsTrigger value="estimates" className="data-[state=active]:bg-brand-clay data-[state=active]:text-white"><Inbox size={16} className="mr-1.5" /> Estimates ({estimates.length})</TabsTrigger>
            <TabsTrigger value="messages" className="data-[state=active]:bg-brand-clay data-[state=active]:text-white"><MessageSquare size={16} className="mr-1.5" /> Messages ({messages.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="estimates" className="mt-6">
            {estimates.length === 0 ? (
              <Empty label="No estimate requests yet." />
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {estimates.map((e) => (
                  <div key={e.id} className="bg-white rounded-2xl border border-brand-line shadow-sm p-6">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-semibold text-brand-charcoal text-lg">{e.firstName} {e.lastName}</h3>
                      <span className="text-xs px-2.5 py-1 rounded-full bg-brand-clay/10 text-brand-clay font-medium capitalize">{e.status}</span>
                    </div>
                    <p className="text-xs text-brand-stone mt-1">{fmt(e.created_at)}</p>
                    <div className="mt-4 space-y-2 text-sm text-brand-charcoal/80">
                      <p className="flex items-center gap-2"><Mail size={14} className="text-brand-clay" /> {e.email}</p>
                      <p className="flex items-center gap-2"><Phone size={14} className="text-brand-clay" /> {e.phone} · Prefers: {e.contactMethod}</p>
                      <p className="flex items-center gap-2"><MapPin size={14} className="text-brand-clay" /> {e.zip || '—'} · Interest: {e.interest || 'Not specified'}</p>
                    </div>
                    {e.message && <p className="mt-3 text-sm text-brand-stone bg-brand-cream rounded-lg p-3 leading-relaxed">{e.message}</p>}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="messages" className="mt-6">
            {messages.length === 0 ? (
              <Empty label="No messages yet." />
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {messages.map((m) => (
                  <div key={m.id} className="bg-white rounded-2xl border border-brand-line shadow-sm p-6">
                    <h3 className="font-semibold text-brand-charcoal text-lg">{m.name}</h3>
                    <p className="text-xs text-brand-stone mt-1">{fmt(m.created_at)}</p>
                    <div className="mt-4 space-y-2 text-sm text-brand-charcoal/80">
                      <p className="flex items-center gap-2"><Mail size={14} className="text-brand-clay" /> {m.email}</p>
                      {m.phone && <p className="flex items-center gap-2"><Phone size={14} className="text-brand-clay" /> {m.phone}</p>}
                    </div>
                    <p className="mt-3 text-sm text-brand-stone bg-brand-cream rounded-lg p-3 leading-relaxed">{m.message}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function Empty({ label }) {
  return (
    <div className="bg-white rounded-2xl border border-brand-line shadow-sm p-16 text-center">
      <Inbox size={40} className="mx-auto text-brand-sand" />
      <p className="text-brand-stone mt-4">{label}</p>
    </div>
  );
}
